import {
  BookOpen,
  Calendar,
  Download,
  Edit3,
  Eye,
  MoreVertical,
  Sparkles,
  Trash2,
  Users,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "../../contexts/LanguageContext";
import { cn } from "../../utils/cn";
import Badge from "../ui/Badge";
import Card from "../ui/Card";
import Tooltip from "../ui/Tooltip";

export interface EbookEntry {
  ebook_id: string;
  title: string;
  subtitle?: string;
  description: string;
  template: string;
  status: string;
  target_audience: string;
  estimated_read_time: number;
  word_count: number;
  download_count: number;
  lead_count: number;
  author?: string;
  company?: string;
  created_at: string;
  published_at?: string;
}

interface EbookCardProps {
  ebook: EbookEntry;
  onView?: (ebook: EbookEntry) => void;
  onEdit?: (ebook: EbookEntry) => void;
  onDelete?: (ebook: EbookEntry) => void;
  onDownload?: (ebookId: string) => void;
  onViewLeads?: (ebook: EbookEntry) => void;
}

const getTemplateIcon = (template: string) => {
  switch (template) {
    case "business_guide":
      return <BookOpen className="h-5 w-5" />;
    case "technical_tutorial":
      return <Edit3 className="h-5 w-5" />;
    case "case_study":
      return <Sparkles className="h-5 w-5" />;
    case "whitepaper":
      return <FileTextIcon />;
    case "checklist":
      return <CheckCircleIcon />;
    default:
      return <BookOpen className="h-5 w-5" />;
  }
};

const FileTextIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="lucide lucide-file-text"
  >
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    <path d="M10 9H8" />
    <path d="M16 13H8" />
    <path d="M16 17H8" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="lucide lucide-check-circle-2"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const getTemplateLabel = (template: string, t: (key: string) => string) => {
  switch (template) {
    case "business_guide":
      return t("Guia de Negócios");
    case "technical_tutorial":
      return t("Tutorial Técnico");
    case "case_study":
      return t("Estudo de Caso");
    case "whitepaper":
      return "Whitepaper";
    case "checklist":
      return t("Checklist");
    default:
      return template;
  }
};

const getStatusColors = (status: string) => {
  switch (status?.toLowerCase()) {
    case "published":
      return {
        bg: "bg-emerald-50",
        text: "text-emerald-700",
        border: "border-emerald-100",
        accent: "bg-emerald-500",
        glow: "group-hover:shadow-[0_15px_40px_rgba(16,185,129,0.12)]",
      };
    case "draft":
      return {
        bg: "bg-amber-50",
        text: "text-amber-700",
        border: "border-amber-100",
        accent: "bg-amber-500",
        glow: "group-hover:shadow-[0_15px_40px_rgba(245,158,11,0.12)]",
      };
    case "generating":
      return {
        bg: "bg-blue-50",
        text: "text-blue-700",
        border: "border-blue-100",
        accent: "bg-blue-500",
        glow: "group-hover:shadow-[0_15px_40px_rgba(59,130,246,0.12)]",
      };
    default:
      return {
        bg: "bg-gray-50",
        text: "text-gray-700",
        border: "border-purple-200",
        accent: "bg-gray-500",
        glow: "group-hover:shadow-[0_15px_40px_rgba(107,114,128,0.12)]",
      };
  }
};

const EbookCard: React.FC<EbookCardProps> = ({
  ebook,
  onView,
  onEdit,
  onDelete,
  onDownload,
  onViewLeads,
}) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const colors = getStatusColors(ebook.status);

  // Fechar menu ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === "Escape") setIsMenuOpen(false);
      };
      document.addEventListener("keydown", handleEscape);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("keydown", handleEscape);
      };
    }
  }, [isMenuOpen]);

  const handleCardClick = (e: React.MouseEvent) => {
    if (
      buttonRef.current?.contains(e.target as Node) ||
      menuRef.current?.contains(e.target as Node) ||
      (e.target as HTMLElement).closest("button:not(.preview-trigger)")
    ) {
      return;
    }
    if (onView) {
      onView(ebook);
    }
  };

  return (
    <Card
      className={cn(
        "group relative flex flex-col h-full bg-white transition-all duration-300",
        "border border-purple-200 hover:border-purple-300",
        colors.glow,
        "hover:scale-[1.02]",
        "cursor-pointer overflow-hidden p-0 rounded-2xl",
        isMenuOpen && "z-40",
      )}
      onClick={handleCardClick}
    >
      {/* Indicador Lateral Baseado no Status */}
      <div
        className={cn(
          "absolute left-0 top-0 bottom-0 w-1 z-20 transition-all duration-300",
          colors.accent,
          "group-hover:w-1.5",
        )}
      />

      {/* Dropdown Menu Toggle */}
      <div className="absolute top-1 right-1 z-30">
        <Tooltip content={t("Mais opções")} position="left">
          <button
            ref={buttonRef}
            onClick={(e) => {
              e.stopPropagation();
              setIsMenuOpen(!isMenuOpen);
            }}
            className={cn(
              "p-1.5 rounded-xl transition-all duration-200",
              "hover:bg-gray-100 active:scale-95",
              "text-gray-400 hover:text-gray-900 bg-white/90 shadow-xs border border-purple-200",
              isMenuOpen &&
                "bg-purple-600 text-white border-purple-600 shadow-md",
            )}
          >
            <MoreVertical className="h-4 w-4" />
          </button>
        </Tooltip>

        {/* Dropdown de Opções Glassmorphism */}
        {isMenuOpen && (
          <div
            ref={menuRef}
            className="absolute right-0 mt-2 w-48 bg-white/95 backdrop-blur-xl rounded-xl shadow-xl border border-purple-200 py-1.5 z-60 animate-modal-scale-in"
            role="menu"
            onClick={(e) => e.stopPropagation()}
          >
            {onEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMenuOpen(false);
                  onEdit(ebook);
                }}
                className="w-full px-3 py-2 text-left text-xs font-bold text-gray-700 hover:bg-purple-600 hover:text-white transition-all flex items-center gap-2.5 group/item"
                role="menuitem"
              >
                <div className="p-1.5 rounded-lg bg-purple-50 group-hover/item:bg-white/20 transition-colors">
                  <Edit3 className="h-3.5 w-3.5 text-purple-600 group-hover/item:text-white" />
                </div>
                {t("Editar Ebook")}
              </button>
            )}

            {onDownload && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMenuOpen(false);
                  onDownload(ebook.ebook_id);
                }}
                className="w-full px-3 py-2 text-left text-xs font-bold text-gray-700 hover:bg-blue-600 hover:text-white transition-all flex items-center gap-2.5 group/down"
                role="menuitem"
              >
                <div className="p-1.5 rounded-lg bg-blue-50 group-hover/down:bg-white/20 transition-colors">
                  <Download className="h-3.5 w-3.5 text-blue-600 group-hover/down:text-white" />
                </div>
                {t("Baixar PDF")}
              </button>
            )}

            {onViewLeads && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMenuOpen(false);
                  onViewLeads(ebook);
                }}
                className="w-full px-3 py-2 text-left text-xs font-bold text-gray-700 hover:bg-emerald-600 hover:text-white transition-all flex items-center gap-2.5 group/leads"
                role="menuitem"
              >
                <div className="p-1.5 rounded-lg bg-emerald-50 group-hover/leads:bg-white/20 transition-colors">
                  <Users className="h-3.5 w-3.5 text-emerald-600 group-hover/leads:text-white" />
                </div>
                {t("Ver Leads ({count})", { count: ebook.lead_count })}
              </button>
            )}

            {onDelete && (
              <>
                <div className="h-px bg-gray-100 my-1 mx-2" />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsMenuOpen(false);
                    onDelete(ebook);
                  }}
                  className="w-full px-3 py-2 text-left text-xs font-bold text-red-600 hover:bg-red-600 hover:text-white transition-all flex items-center gap-2.5 group/del"
                  role="menuitem"
                >
                  <div className="p-1.5 rounded-lg bg-red-50 group-hover/del:bg-white/20 transition-colors">
                    <Trash2 className="h-3.5 w-3.5 text-red-600 group-hover/del:text-white" />
                  </div>
                  {t("Excluir")}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      <div className="p-3.5 flex flex-col h-full gap-3 relative">
        {/* Cabeçalho do Card */}
        <div className="flex items-start gap-2.5">
          {/* Capa/Ãcone do Ebook */}
          <div
            className={cn(
              "shrink-0 w-11 h-11 flex items-center justify-center rounded-xl shadow-md ring-2 ring-white transition-all duration-300 group-hover:scale-105",
              "bg-linear-to-br from-purple-600 to-indigo-700 text-white",
            )}
          >
            {getTemplateIcon(ebook.template)}
          </div>

          <div className="flex-1 min-w-0 pt-0.5">
            <div className="flex flex-wrap gap-1.5 mb-1.5">
              <Badge
                variant="outline"
                size="sm"
                className="font-bold uppercase tracking-wider text-[9px] px-2 py-0.5 bg-purple-50 text-purple-700 border-transparent"
              >
                {getTemplateLabel(ebook.template, t)}
              </Badge>
              <Badge
                variant="outline"
                size="sm"
                className={cn(
                  "font-bold uppercase text-[9px] px-2 py-0.5 border-transparent",
                  colors.bg,
                  colors.text,
                )}
              >
                {ebook.status}
              </Badge>
            </div>
            <h3 className="text-gray-900 font-bold text-sm sm:text-base leading-tight line-clamp-2 group-hover:text-purple-700 transition-colors duration-200">
              {ebook.title}
            </h3>
          </div>
        </div>

        {/* Descrição Curta */}
        <div className="flex-1">
          <p className="text-[11px] sm:text-xs text-gray-500 line-clamp-3 leading-relaxed font-medium bg-gray-50/50 p-3 rounded-xl border border-purple-200 group-hover:border-purple-200 transition-all relative overflow-hidden">
            {ebook.description ||
              t("Nenhuma descrição disponível para este ebook.")}
            <span className="absolute bottom-0 left-0 right-0 h-4 bg-linear-to-t from-gray-50/50 to-transparent group-hover:from-white/0" />
          </p>
        </div>

        {/* Footer do Card - Métricas */}
        <div className="flex flex-col gap-3 pt-3.5 border-t border-purple-200">
          <div className="flex flex-wrap items-center justify-between gap-2">
            {/* Data */}
            <div className="flex items-center gap-1.5 text-[10px] text-gray-400 font-bold bg-gray-50 px-2 py-1 rounded-lg border border-purple-200">
              <Calendar className="h-3 w-3" />
              {new Date(ebook.created_at).toLocaleDateString("pt-BR", {
                day: "2-digit",
                month: "short",
              })}
            </div>

            {/* Estatísticas Rápidas */}
            <div className="flex items-center gap-1.5">
              <Tooltip
                content={t("{count} Downloads", {
                  count: ebook.download_count,
                })}
              >
                <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 rounded-lg border border-blue-100 text-[10px] font-bold text-blue-700">
                  <Download className="h-3 w-3" />
                  <span>{ebook.download_count}</span>
                </div>
              </Tooltip>

              <Tooltip
                content={t("{count} Leads", { count: ebook.lead_count })}
              >
                <div className="flex items-center gap-1 px-2 py-1 bg-emerald-50 rounded-lg border border-emerald-100 text-[10px] font-bold text-emerald-700">
                  <Users className="h-3 w-3" />
                  <span>{ebook.lead_count}</span>
                </div>
              </Tooltip>
            </div>
          </div>

          {/* Botão Visual de Ação */}
          <div className="h-8 overflow-hidden relative rounded-lg bg-gray-50/50 group-hover:bg-purple-50/50 transition-colors">
            <div className="absolute inset-0 z-10 flex items-center justify-center gap-2 bg-purple-50/50 text-purple-600 font-bold text-[10px] transition-all duration-300 translate-y-8 group-hover:translate-y-0 uppercase tracking-wider">
              <Eye className="h-3 w-3" />
              {t("Visualizar e Editar")}
            </div>
            <div className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-gray-300 tracking-widest transition-all duration-300 group-hover:opacity-0 uppercase">
              {t("Clique para abrir")}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default EbookCard;
